import { useState, useMemo } from 'react'
import {
  ChevronRight,
  ChevronDown,
  FileText,
  Folder,
  MoreVertical,
  Clock,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRelativeDate } from '@/lib/formatRelativeDate'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Document, DocumentCategory } from '@/types/api'
import type { ExplorerDoc } from './types'

/* ── Category display names ────────────────────────────────── */

type ExplorerCategoryId =
  | 'strategy_vision'
  | 'market_research'
  | 'product'
  | 'customers'
  | 'go_to_market'
  | 'team_ops'
  | 'financials'
  | 'unsorted'

const CATEGORY_LABELS: Record<ExplorerCategoryId, string> = {
  strategy_vision: 'Strategy & Vision',
  market_research: 'Market Research',
  product: 'Product',
  customers: 'Customers',
  go_to_market: 'Go-to-Market',
  team_ops: 'Team & Ops',
  financials: 'Financials',
  unsorted: 'Unsorted',
}

const CATEGORY_ORDER: ExplorerCategoryId[] = [
  'strategy_vision',
  'market_research',
  'product',
  'customers',
  'go_to_market',
  'team_ops',
  'financials',
  'unsorted',
]

/* ── Types ─────────────────────────────────────────────────── */

interface SubCategory {
  id: string
  name: string
  docs: ExplorerDoc[]
}

interface Category {
  id: ExplorerCategoryId
  name: string
  count: number
  subcategories: SubCategory[]
}

interface ContextExplorerProps {
  /** Documents from API — when empty, falls back to mock data */
  documents?: Document[]
  onSelectDoc: (doc: ExplorerDoc) => void
}

/* ── Helpers ───────────────────────────────────────────────── */

/** Convert a Document to an ExplorerDoc */
function mapToExplorerDoc(doc: Document): ExplorerDoc {
  return {
    id: doc.id,
    title: doc.name,
    type: doc.type === 'pdf' ? 'pdf' : doc.type === 'audio' ? 'audio' : 'text',
    confidence: doc.extractedData?.categoryConfidence,
    category: doc.extractedData?.category,
    subCategory: doc.extractedData?.subCategory,
    uploadedAt: doc.uploadedAt,
  }
}

const CUSTOMER_HINTS = [
  'persona',
  'customer',
  'interview',
  'feedback',
  'user',
  'retention',
  'churn',
]

const STRATEGY_HINTS = [
  'mission',
  'vision',
  'strategy',
  'north star',
  'one pager',
  'deck',
  'pitch',
]

function includesAny(text: string, hints: string[]): boolean {
  return hints.some((hint) => text.includes(hint))
}

function mapExplorerCategory(doc: Document): ExplorerCategoryId {
  const rawCategory: DocumentCategory = doc.extractedData?.category ?? 'unsorted'
  const descriptor = `${doc.name ?? ''} ${doc.extractedData?.subCategory ?? ''}`.toLowerCase()

  if (rawCategory === 'product_engineering') return 'product'
  if (rawCategory === 'go_to_market') return 'go_to_market'
  if (rawCategory === 'team_ops') return 'team_ops'
  if (rawCategory === 'unsorted') return 'unsorted'

  if (rawCategory === 'customer_intelligence') {
    return includesAny(descriptor, CUSTOMER_HINTS) ? 'customers' : 'market_research'
  }

  if (rawCategory === 'fundraising_finance') {
    return includesAny(descriptor, STRATEGY_HINTS) ? 'strategy_vision' : 'financials'
  }

  if (rawCategory === 'legal_compliance') {
    return 'strategy_vision'
  }

  return 'unsorted'
}

/** Group documents into the category→subcategory tree structure */
function buildTaxonomy(documents: Document[]): Category[] {
  const grouped = new Map<ExplorerCategoryId, Map<string, ExplorerDoc[]>>()

  for (const doc of documents) {
    const cat = mapExplorerCategory(doc)
    const sub = doc.extractedData?.subCategory ?? 'General'

    if (!grouped.has(cat)) grouped.set(cat, new Map())
    const subs = grouped.get(cat)!
    if (!subs.has(sub)) subs.set(sub, [])
    subs.get(sub)!.push(mapToExplorerDoc(doc))
  }

  return CATEGORY_ORDER.map((catId) => {
    const subs = grouped.get(catId) ?? new Map<string, ExplorerDoc[]>()
    let total = 0
    const subcategories: SubCategory[] = []

    for (const [subName, docs] of subs) {
      total += docs.length
      subcategories.push({
        id: `${catId}__${subName}`,
        name: subName,
        docs,
      })
    }

    return {
      id: catId,
      name: CATEGORY_LABELS[catId],
      count: total,
      subcategories,
    }
  })
}

/* ── Mock data (used when no documents passed) ────────────── */

const MOCK_DOCUMENTS: Document[] = [
  {
    id: 'd1',
    uid: 'mock-user',
    type: 'pdf',
    name: 'Series A Deck.pdf',
    status: 'completed',
    extractedData: {
      category: 'fundraising_finance',
      subCategory: 'Pitch Decks',
      categoryConfidence: 0.92,
    },
  },
  {
    id: 'd2',
    uid: 'mock-user',
    type: 'text',
    name: 'Competitor Matrix.xlsx',
    status: 'completed',
    extractedData: {
      category: 'customer_intelligence',
      subCategory: 'Competitor Analysis',
      categoryConfidence: 0.88,
    },
  },
  {
    id: 'd3',
    uid: 'mock-user',
    type: 'text',
    name: 'User Interview Transcript',
    status: 'completed',
    extractedData: {
      category: 'customer_intelligence',
      subCategory: 'User Personas',
      categoryConfidence: 0.86,
    },
  },
  {
    id: 'd4',
    uid: 'mock-user',
    type: 'text',
    name: 'Q3 Product Roadmap',
    status: 'completed',
    extractedData: {
      category: 'product_engineering',
      subCategory: 'Roadmap',
      categoryConfidence: 0.9,
    },
  },
  {
    id: 'd5',
    uid: 'mock-user',
    type: 'text',
    name: 'Meeting Notes_2024.txt',
    status: 'completed',
    extractedData: {
      category: 'unsorted',
      subCategory: 'Inbox',
      categoryConfidence: 0.4,
    },
  },
]

const MOCK_TAXONOMY: Category[] = buildTaxonomy(MOCK_DOCUMENTS)

/* ── Component ─────────────────────────────────────────────── */

export function ContextExplorer({
  documents,
  onSelectDoc,
}: ContextExplorerProps) {
  const taxonomy = useMemo(
    () => (documents ? buildTaxonomy(documents) : MOCK_TAXONOMY),
    [documents]
  )

  const recentDocs = useMemo(() => {
    if (!documents || documents.length === 0) return []
    return documents.slice(0, 5).map(mapToExplorerDoc)
  }, [documents])

  const [expandedCats, setExpandedCats] = useState<string[]>(() =>
    taxonomy.length > 0 ? [taxonomy[0].id] : []
  )
  const [expandedSubs, setExpandedSubs] = useState<string[]>(() =>
    taxonomy.length > 0 && taxonomy[0].subcategories.length > 0
      ? [taxonomy[0].subcategories[0].id]
      : []
  )

  const toggleCat = (id: string) => {
    setExpandedCats((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  const toggleSub = (id: string) => {
    setExpandedSubs((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]))
  }

  return (
    <ScrollArea className="flex-1 h-full">
      <div className="px-2 py-2 space-y-1">
        {/* Recent Documents Section */}
        {recentDocs.length > 0 && (
          <div className="mb-4">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" />
              Recent Uploads
            </div>
            <div className="space-y-0.5 mt-1">
              {recentDocs.map((doc) => (
                <DocRow
                  key={`recent-${doc.id}`}
                  doc={doc}
                  onSelectDoc={onSelectDoc}
                  showUploadDate
                />
              ))}
            </div>
            <Separator className="my-3 bg-border/50" />
          </div>
        )}

        {/* Category Tree */}
        {taxonomy.map((cat) => (
          <div key={cat.id} className="select-none">
            {/* Category Header */}
            <button
              onClick={() => toggleCat(cat.id)}
              className={cn(
                'w-full flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-secondary/50 group transition-colors',
                cat.id === 'unsorted' && 'text-accent-warning'
              )}
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                {expandedCats.includes(cat.id) ? (
                  <ChevronDown className="h-4 w-4 opacity-50" />
                ) : (
                  <ChevronRight className="h-4 w-4 opacity-50" />
                )}
                <span className={cn(cat.id === 'unsorted' && 'italic')}>{cat.name}</span>
              </div>
              <Badge
                variant="secondary"
                className="h-5 px-1.5 text-[10px] bg-secondary/50 text-muted-foreground group-hover:bg-background"
              >
                {cat.count}
              </Badge>
            </button>

            {/* Subcategories */}
            {expandedCats.includes(cat.id) && (
              <div className="ml-2 pl-2 border-l border-border/30 mt-1 space-y-1">
                {cat.subcategories.length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    No documents yet
                  </div>
                )}
                {cat.subcategories.map((sub) => (
                  <div key={sub.id}>
                    <button
                      onClick={() => toggleSub(sub.id)}
                      className="w-full flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-secondary/50 text-muted-foreground hover:text-foreground text-sm transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Folder
                          className={cn(
                            'h-3.5 w-3.5 opacity-70',
                            expandedSubs.includes(sub.id) && 'fill-current'
                          )}
                        />
                        <span>{sub.name}</span>
                      </div>
                      <span className="text-[10px] opacity-50">{sub.docs.length}</span>
                    </button>

                    {/* Documents */}
                    {expandedSubs.includes(sub.id) && (
                      <div className="ml-4 space-y-0.5 mt-1">
                        {sub.docs.map((doc) => (
                          <DocRow
                            key={doc.id}
                            doc={doc}
                            onSelectDoc={onSelectDoc}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  )
}

/* ── Document Row ──────────────────────────────────────────── */

function DocRow({
  doc,
  onSelectDoc,
  showUploadDate = false,
}: {
  doc: ExplorerDoc
  onSelectDoc: (doc: ExplorerDoc) => void
  showUploadDate?: boolean
}) {
  return (
    <div
      className="group flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-primary/10 cursor-pointer text-sm"
      onClick={() => onSelectDoc(doc)}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        <FileText className="h-3.5 w-3.5 text-primary shrink-0" />
        <span className="truncate opacity-90 group-hover:text-primary transition-colors">
          {doc.title}
        </span>

        {/* Confidence Badge */}
        {doc.confidence != null && (
          <Badge
            variant="outline"
            className={cn(
              'h-4 px-1 text-[9px] shrink-0',
              doc.confidence >= 0.85
                ? 'border-green-500/50 text-green-600'
                : doc.confidence >= 0.65
                  ? 'border-yellow-500/50 text-yellow-600'
                  : 'border-red-500/50 text-red-600'
            )}
          >
            {Math.round(doc.confidence * 100)}%
          </Badge>
        )}

        {/* Upload Date — shown in Recent section */}
        {showUploadDate && doc.uploadedAt && (
          <span className="text-[10px] text-muted-foreground shrink-0">
            {formatRelativeDate(doc.uploadedAt)}
          </span>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 -mr-1"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onSelectDoc(doc)}>
            Use in Chat
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
